import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowRight, Calendar, MapPin, TrendingUp, Sparkles, Sun } from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import SEO from "../components/SEO";
import CityConciergerieSection from "../components/CityConciergerieSection";
import CityLocalGuide from "../components/CityLocalGuide";
import { CityTeamBlock } from "../components/AboutTeam";
import { apiClient } from "../lib/api";

const renderBold = (t) => t.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 ? <strong key={i} className="text-foreground">{part}</strong> : part));

const geoDist = (a, b) => (a.lat - b.lat) ** 2 + ((a.lng - b.lng) * 0.66) ** 2;

export default function CityDetailPage() {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [allCities, setAllCities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiClient
      .get(`/cities/${slug}`)
      .then((r) => setCity(r.data))
      .catch(() => setCity(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    apiClient.get("/cities").then((r) => setAllCities(r.data)).catch(() => {});
  }, []);

  if (loading) {
    return (
      <PublicLayout>
        <div className="container-tm py-32 text-center text-muted-foreground">Chargement…</div>
      </PublicLayout>
    );
  }
  if (!city) {
    return (
      <PublicLayout>
        <div className="container-tm py-32 text-center">
          <h1 className="h-section">Ville introuvable</h1>
          <Link to="/villes" className="text-accent mt-4 inline-block">← Retour au comparateur</Link>
        </div>
      </PublicLayout>
    );
  }

  const buckets = [
    { key: "studio", label: "Studio", emoji: "" },
    { key: "t2", label: "T2, 1 chambre", emoji: "" },
    { key: "t3", label: "T3, 2 chambres", emoji: "" },
    { key: "t4", label: "T4+, 3 ch. et plus", emoji: "" },
  ];

  return (
    <PublicLayout>
      <SEO
        title={city.seo_title}
        description={city.seo_description}
        image={city.image}
      />
      {/* HERO */}
      <section className="pt-6 md:pt-8 pb-10" data-testid="city-hero">
        <div className="container-tm">
          <nav className="text-sm text-foreground/80 mb-6 flex flex-wrap gap-1.5" aria-label="Fil d'Ariane" data-testid="breadcrumb">
            <Link to="/" className="hover:text-accent">Accueil</Link> ·
            <Link to="/villes" className="hover:text-accent" data-testid="back-to-cities">Nos communes</Link> ·
            <Link to={`/departements/${city.department_slug}`} className="hover:text-accent" data-testid="breadcrumb-department">{city.department}</Link> ·
            <span className="font-bold text-primary">{city.name}</span>
          </nav>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 relative">
              <div className="relative aspect-[16/11] blob overflow-hidden bg-secondary">
                <img src={city.image} alt={`${city.name}, location Airbnb et conciergerie`} className="w-full h-full object-cover" />
                {city.image_credit && <div className="absolute bottom-3 right-5 text-[10px] text-white/80 drop-shadow" data-testid="city-image-credit">{city.image_credit}</div>}
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="absolute -left-16 top-0 w-40 h-40 rounded-full bg-secondary -z-10 hidden lg:block" />
              <div className="flex items-center gap-2 text-sm font-bold text-[hsl(var(--ocean))] uppercase tracking-[0.12em]">
                <MapPin size={14} /> {city.department}{city.postal_code ? ` · ${city.postal_code}` : ""}
              </div>
              <h1 className="h-display mt-3">Conciergerie Airbnb et location courte durée à {city.name}</h1>
              <p className="mt-5 text-muted-foreground text-lg leading-relaxed">{city.tagline} Combien rapporte un Airbnb à {city.name} et quelle conciergerie choisir ? Toutes les réponses ci-dessous.</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to={`/devis?ville=${city.slug}`} className="btn-accent" data-testid="city-hero-devis">Je demande un devis</Link>
                <Link to="/simulateur" className="btn-sun" data-testid="city-hero-simulateur">Faites estimer !</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KPI cards */}
      <section className="py-8">
        <div className="container-tm grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          {[
            { icon: TrendingUp, label: "Occupation moyenne", value: `${city.occupancy_rate}%`, accent: "accent" },
            { icon: Calendar, label: "Prix moyen / nuit", value: `${city.adr} €`, accent: "sun" },
            { icon: Sparkles, label: "Score investisseur", value: `${city.investment_score.toFixed(1)}/10`, accent: "ocean" },
            { icon: MapPin, label: "Population", value: city.population.toLocaleString("fr-FR"), accent: "accent" },
          ].map((k) => (
            <div key={k.label} className="surface-card p-5 md:p-7" data-testid={`kpi-${k.label}`}>
              <span className="inline-flex w-10 h-10 rounded-full bg-accent/10 text-accent items-center justify-center">
                <k.icon size={18} />
              </span>
              <div className="text-xs md:text-sm text-muted-foreground mt-4 font-medium">{k.label}</div>
              <div className="mt-1 font-bold text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "Quicksand, sans-serif", letterSpacing: "-0.01em" }}>
                {k.value}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Revenue by size */}
      <section className="py-16">
        <div className="container-tm grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="pill">Revenus annuels</span>
            <h2 className="h-section mt-4">
              Combien selon<br />la <span className="text-accent">taille du bien</span> ?
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">{renderBold(city.description)}</p>
            <Link to="/simulateur" className="btn-accent mt-8" data-testid="city-simulateur-cta">
              Simuler mon bien <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-7 space-y-3">
            {buckets.map((b) => {
              const [lo, hi] = city.revenue_by_size[b.key];
              return (
                <div key={b.key} className="surface-card p-5 md:p-6 flex items-center justify-between gap-4" data-testid={`bucket-${b.key}`}>
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">{b.emoji}</span>
                    <div>
                      <div className="font-bold text-lg">{b.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Estimation marché {city.name}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-xl md:text-2xl tracking-tight" style={{ fontFamily: "Quicksand, sans-serif", letterSpacing: "-0.01em" }}>
                      {lo.toLocaleString("fr-FR")} – {hi.toLocaleString("fr-FR")}&nbsp;€
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">par an</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Seasonality chart */}
      <section className="py-16 bg-secondary/50 rounded-3xl mx-3 md:mx-6">
        <div className="container-tm">
          <div className="max-w-2xl">
            <span className="pill">Saisonnalité</span>
            <h2 className="h-section mt-4">Revenus mois par mois<br />à <span className="text-accent">{city.name}</span></h2>
            <p className="text-muted-foreground mt-4">
              Comprendre la saisonnalité est clé pour fixer vos tarifs. Revenus moyens d&apos;un T2/T3 sur l&apos;année.
            </p>
          </div>
          <div className="mt-10 bg-white rounded-3xl p-5 md:p-9 border border-border">
            <ResponsiveContainer width="100%" height={340}>
              <AreaChart data={city.seasonality} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="seasonalGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(13, 100%, 62%)" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="hsl(13, 100%, 62%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(30, 25%, 90%)" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "hsl(222, 14%, 45%)", fontSize: 12, fontWeight: 500 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: "hsl(222, 14%, 45%)", fontSize: 12 }} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: "white", border: "1px solid hsl(30, 25%, 90%)", borderRadius: 12, fontSize: 13 }}
                  formatter={(v, name) => (name === "revenue" ? [`${v.toLocaleString("fr-FR")} €`, "Revenus"] : [`${v}%`, "Occupation"])}
                />
                <Area type="monotone" dataKey="revenue" stroke="hsl(13, 100%, 62%)" strokeWidth={3} fill="url(#seasonalGradient)" />
              </AreaChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-4 md:grid-cols-12 gap-3 mt-8">
              {city.seasonality.map((m) => (
                <div key={m.month} className="text-center">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">{m.month}</div>
                  <div className="text-sm font-bold mt-1">{m.occupancy}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Events */}
      <section className="py-16 md:py-24">
        <div className="container-tm">
          <span className="pill">Événements rentables</span>
          <h2 className="h-section mt-4 max-w-2xl">
            Les rendez-vous à <span className="text-accent">ne pas manquer</span>
          </h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {city.events.map((e) => (
              <motion.div
                key={e.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="surface-card p-7 hover:border-accent transition-colors"
                data-testid={`event-${e.name}`}
              >
                <Sun className="text-[hsl(var(--ocean))]" size={22} fill="currentColor" />
                <h3 className="font-bold text-xl mt-4">{e.name}</h3>
                <div className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5">
                  <Calendar size={13} /> {e.period}
                </div>
                <div className="mt-4 pt-4 border-t border-border inline-flex items-center gap-2 bg-accent/10 text-accent px-3 py-1.5 rounded-full text-xs font-bold">
                  {e.impact}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="py-16 md:py-24">
        <div className="container-tm">
          <div className="rounded-3xl bg-primary text-primary-foreground p-8 md:p-14 lg:p-20 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <span className="pill bg-white/10 text-white">Conseils de pros</span>
              <h2 className="h-section mt-4 text-white">
                Maximiser <span className="text-[hsl(var(--ocean))]">{city.name}</span>
              </h2>
              <p className="mt-5 text-white/70 leading-relaxed">
                Nos recommandations pour mieux louer votre bien à {city.name}.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-5">
                {city.tips.map((t, i) => (
                  <li key={i} className="flex gap-5 border-b border-white/10 pb-5 last:border-0">
                    <span className="font-bold text-3xl text-[hsl(var(--ocean))] shrink-0 leading-none" style={{ fontFamily: "Quicksand, sans-serif" }}>
                      0{i + 1}
                    </span>
                    <p className="text-white/90 leading-relaxed">{t}</p>
                  </li>
                ))}
              </ul>
              <Link to={`/devis?ville=${city.slug}`} className="btn-accent mt-10" data-testid="city-conciergerie-cta">
                Comparer les conciergeries à {city.name} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CityTeamBlock city={city.name} />

      <CityLocalGuide city={city} />
      <CityConciergerieSection
        city={city}
        nearby={allCities
          .filter((c) => c.slug !== city.slug)
          .sort((a, b) => geoDist(a, city) - geoDist(b, city))
          .slice(0, 12)}
      />
    </PublicLayout>
  );
}
